import type { IAddress, ITrip, ITripRoutePoint } from '@curbnturf/objects';
import type { ITripPlan, ITripStop, TripStopKind } from './trip-planner.service';

/**
 * Maps a local trip plan to the real backend's `ITrip` shape (POST/PUT
 * `/api/v1/route/trip`). Route points are always sent without an `id` —
 * the server's `TripService.updatePoints()` treats id-less points as
 * inserts and deletes any previously-saved point missing from the payload,
 * so every sync fully replaces the route. Simple, and fine for a
 * single-owner, non-collaborative trip.
 */
export function planToApiTrip(plan: ITripPlan): ITrip {
  return {
    id: plan.remoteId,
    name: plan.name,
    width: plan.corridorMiles ?? 0,
    route: plan.stops.map((stop, index) => stopToApiPoint(stop, index)),
  };
}

/**
 * `refId` is only a real backend id for `'private'`/`'boondocking'` stops
 * (ListingCatalogService now sources those from the live backend search).
 * `'poi'` stops carry `IPoi.id`, a local/external string unrelated to the
 * `activity_location` table, so `poiId` is deliberately never set — same
 * for `'custom'` pin-drops, which have no backend ref at all. Both still
 * sync fine as plain coordinate/name points.
 */
export function stopToApiPoint(stop: ITripStop, ordinality: number): ITripRoutePoint {
  const point: ITripRoutePoint = {
    name: stop.name,
    coordinates: { lat: stop.lat, lon: stop.lng },
    ordinality,
    arrival: stop.checkInDate,
    departure: stop.checkOutDate,
  };
  const address = stopAddress(stop);
  if (address) point.address = address;
  if (stop.kind === 'private' && typeof stop.refId === 'number') {
    point.listingId = stop.refId;
  } else if (stop.kind === 'boondocking' && typeof stop.refId === 'number') {
    point.boondockId = stop.refId;
  }
  return point;
}

/** Prefer the structured parts; otherwise the display string goes in the
 *  street line so it still round-trips (see `apiPointToStop`). */
function stopAddress(stop: ITripStop): IAddress | undefined {
  if (stop.addressParts) return stop.addressParts;
  if (stop.address) return { address: stop.address, city: '', state: '', zipcode: '' };
  return undefined;
}

/** Reverse mapping for the pull/merge path — kind is inferred from which
 *  ref id (if any) the server sent back, since the backend has no `kind`
 *  concept of its own. Stops come back without an `id` — the caller mints
 *  fresh local stop ids when materializing this into a real `ITripPlan`. */
export function apiTripToPlan(
  trip: ITrip,
): Omit<ITripPlan, 'id' | 'createdAt' | 'updatedAt' | 'stops'> & { stops: Omit<ITripStop, 'id'>[] } {
  return {
    remoteId: trip.id,
    name: trip.name || 'Untitled trip',
    corridorMiles: trip.width,
    stops: (trip.route ?? [])
      .slice()
      .sort((a, b) => (a.ordinality ?? 0) - (b.ordinality ?? 0))
      .map(apiPointToStop),
  };
}

function apiPointToStop(point: ITripRoutePoint): Omit<ITripStop, 'id'> {
  let kind: TripStopKind = 'custom';
  let refId: number | undefined;
  if (point.listingId) {
    kind = 'private';
    refId = point.listingId;
  } else if (point.boondockId) {
    kind = 'boondocking';
    refId = point.boondockId;
  }
  const parts = point.address;
  const hasAddress = !!parts && [parts.address, parts.city, parts.state, parts.zipcode].some(Boolean);
  return {
    kind,
    refId,
    name: point.name || '',
    ...(hasAddress && parts
      ? {
          address: [parts.address, parts.city, [parts.state, parts.zipcode].filter(Boolean).join(' ')]
            .filter(Boolean)
            .join(', '),
          addressParts: { address: parts.address ?? '', city: parts.city ?? '', state: parts.state ?? '', zipcode: parts.zipcode ?? '' },
        }
      : {}),
    lat: point.coordinates?.lat ?? 0,
    lng: point.coordinates?.lon ?? 0,
    checkInDate: dateToIso(point.arrival),
    checkOutDate: dateToIso(point.departure),
  };
}

function dateToIso(value: ITripRoutePoint['arrival']): string | undefined {
  if (!value) return undefined;
  if (typeof value === 'string') return value.slice(0, 10);
  return value.toISODate() ?? undefined;
}
